/**
 * Device Modal Theme Service
 * 
 * Provides registry of available Templates, Traits, Attributes and their Styles.
 * Handles reading/writing theme configurations to Firestore.
 */

import { DeviceModalConfig, TraitStyleConfig, AttributeStyleConfig, TemplateStyleId, TraitStyleId } from '../components/deviceModals/types';
import { getUserPreferences, saveUserPreferences } from './userPreferencesApi';
import { logger } from '../utils/logger';

// Helper type for Firestore data (uses plain strings)
interface FirestoreDeviceModalConfig {
  templateId: string;
  templateName?: string; // User-customized display name
  traitStyles?: Record<string, string>;
  attributeStyles?: Record<string, string>;
  iconStyle?: string; // Legacy: Device icon style
  smallIconStyle?: string; // Small icon (title bar)
  bigIconStyle?: string; // Big icon (main display)
  traitOrder?: string[]; // Custom order of trait/attribute components
  hiddenComponents?: string[]; // Hidden components list
}

// New: Per-template storage structure
interface FirestoreDeviceThemeStorage {
  appliedTemplateId: string; // Which template is currently applied
  templateConfigs: Record<string, FirestoreDeviceModalConfig>; // Per-template configs
}

// Type guard to check if data is new format
function isNewStorageFormat(data: any): data is FirestoreDeviceThemeStorage {
  return data && typeof data.appliedTemplateId === 'string' && typeof data.templateConfigs === 'object';
}

// ============================================
// Type Definitions
// ============================================

export type DeviceType = 
  | 'light' 
  | 'hvac' 
  | 'dehumidifier' 
  | 'switch' 
  | 'fan' 
  | 'curtain' 
  | 'blind' 
  | 'power_meter' 
  | 'power_meter_3phase' 
  | 'door_sensor' 
  | 'button'
  | 'th_sensor'
  | 'presence_sensor'
  | 'lux_sensor'
  | 'multi_sensor'
  | 'outlet'
  | 'erv'
  | 'generic';

export type TraitName = 
  | 'OnOff' 
  | 'Brightness' 
  | 'ColorSetting' 
  | 'FanSpeed' 
  | 'Mode' 
  | 'Timer' 
  | 'CustomToggles' 
  | 'HumiditySetting' 
  | 'TemperatureSetting' 
  | 'ThermostatMode' 
  | 'OpenClose';

export type AttributeName = 
  | 'SensorDisplay' 
  | 'SensorChart' 
  | 'StatusIndicator' 
  | 'OccupancyTimeline'
  | 'AQIDisplay';

export interface StyleInfo {
  id: string;           // e.g. 'Style01'
  name: string;         // e.g. 'Button'
  description?: string; // Optional description
}

export interface TemplateInfo {
  id: string;           // e.g. 'Template01'
  name: string;         // e.g. 'Full'
  description?: string;
  defaultConfig?: Partial<DeviceModalConfig>; // Template-specific default config
}

// ============================================
// Template Registry
// ============================================

/**
 * Registry of available templates per device type
 * Note: This is mutable to allow dynamic template management
 */
export let TEMPLATE_REGISTRY: Record<string, TemplateInfo[]> = {
  light: [
    { id: 'Template01', name: 'Full', description: '完整燈光控制（開關、亮度、色溫）' }
  ],
  hvac: [
    { id: 'Template02', name: 'Modern', description: '現代化冷氣控制介面' }
  ],
  th_sensor: [
    { id: 'Template01', name: 'Dashboard', description: '溫濕度感應器儀表板' }
  ],
  presence_sensor: [
    { id: 'Template01', name: 'Dashboard', description: '人體感應器儀表板' }
  ],
  lux_sensor: [
    { id: 'Template01', name: 'Dashboard', description: '光照感應器儀表板' }
  ],
  multi_sensor: [
    { id: 'Template01', name: 'Dashboard', description: '多功能感應器儀表板' }
  ],
  dehumidifier: [
    { 
      id: 'Template01', 
      name: 'Full', 
      description: '完整除濕機控制',
      defaultConfig: {
        smallIconStyle: 'Style01' as any, // WaterDrop
        bigIconStyle: 'Style01' as any, // WaterDrop
        traitStyles: {
          OnOff: 'Style02' as any, // Toggle
          HumiditySetting: 'Style01' as any, // Slider
          Mode: 'Style01' as any, // Pills
          Timer: 'Style01' as any, // GridExpand
          FanSpeed: 'Style01' as any, // Buttons
          CustomToggles: 'Style01' as any // Switches
        },
        attributeStyles: {
          SensorDisplay: 'Style04' as any, // Circle (圓形大顯示)
          StatusIndicator: 'Style01' as any
        }
      }
    },
    { 
      id: 'Template02', 
      name: 'Modern', 
      description: '現代化除濕機介面',
      defaultConfig: {
        smallIconStyle: 'Style03' as any, // Dew
        bigIconStyle: 'Style02' as any, // Humidity
        traitStyles: {
          OnOff: 'Style05' as any, // TextButton
          HumiditySetting: 'Style03' as any, // SliderMarks
          Mode: 'Style03' as any, // IconCircle
          Timer: 'Style02' as any, // GridSimple
          FanSpeed: 'Style02' as any, // Pills
          CustomToggles: 'Style02' as any // HorizontalSwitch
        },
        attributeStyles: {
          SensorDisplay: 'Style04' as any, // Circle (圓形大顯示)
          StatusIndicator: 'Style02' as any // Different status style
        }
      }
    }
  ],
  switch: [
    { id: 'Template01', name: 'Card', description: '卡片式開關控制' }
  ],
  outlet: [
    { id: 'Template01', name: 'Card', description: '卡片式插座控制' }
  ],
  fan: [
    { id: 'Template01', name: 'Modern', description: '現代化風扇控制' }
  ],
  curtain: [
    { id: 'Template01', name: 'Modern', description: '現代化窗簾控制' }
  ],
  blind: [
    { id: 'Template01', name: 'Modern', description: '現代化百葉窗控制' }
  ],
  power_meter: [
    { id: 'Template01', name: 'Dashboard', description: '電力儀表儀表板' }
  ],
  power_meter_3phase: [
    { id: 'Template01', name: 'Dashboard', description: '三相電力儀表儀表板' }
  ],
  door_sensor: [
    { id: 'Template01', name: 'Status', description: '門磁狀態顯示' }
  ],
  button: [
    { id: 'Template01', name: 'Status', description: '按鈕狀態顯示' }
  ],
  erv: [
    { 
      id: 'Template01', 
      name: 'Full', 
      description: '全熱交換機完整控制',
      defaultConfig: {
        smallIconStyle: 'Style01' as any, // Air/Ventilation
        bigIconStyle: 'Style01' as any,
        traitStyles: {
          OnOff: 'Style02' as any, // Toggle
          FanSpeed: 'Style01' as any, // Buttons
          Mode: 'Style01' as any // Pills
        },
        attributeStyles: {
          SensorDisplay: 'Style01' as any, // Card
          AQIDisplay: 'Style01' as any, // Gauge
          StatusIndicator: 'Style01' as any
        }
      }
    }
  ],
  generic: [
    { id: 'Template01', name: 'Auto', description: '自動適應通用模板' }
  ]
};

// ============================================
// Trait Style Registry
// ============================================

/**
 * Registry of available styles per trait
 */
export const TRAIT_STYLE_REGISTRY: Record<TraitName, StyleInfo[]> = {
  OnOff: [
    { id: 'Style01', name: 'Button', description: '按鈕式開關' },
    { id: 'Style02', name: 'Toggle', description: '切換開關' },
    { id: 'Style03', name: 'Card', description: '卡片式開關' },
    { id: 'Style04', name: 'RoundButton', description: '圓形按鈕' },
    { id: 'Style05', name: 'TextButton', description: '文字按鈕' }
  ],
  Brightness: [
    { id: 'Style01', name: 'Slider', description: '水平滑桿' },
    { id: 'Style02', name: 'Vertical', description: '垂直滑桿' }
  ],
  ColorSetting: [
    { id: 'Style01', name: 'Slider', description: '色溫滑桿（含快捷按鈕）' },
    { id: 'Style02', name: 'SliderSimple', description: '色溫滑桿（無快捷按鈕）' }
  ],
  FanSpeed: [
    { id: 'Style01', name: 'Buttons', description: '按鈕組' },
    { id: 'Style02', name: 'Pills', description: '藥丸按鈕' }
  ],
  Mode: [
    { id: 'Style01', name: 'Pills', description: '藥丸按鈕' },
    { id: 'Style02', name: 'Dropdown', description: '下拉選單' },
    { id: 'Style03', name: 'IconCircle', description: '圓形圖標' },
    { id: 'Style04', name: 'Pills', description: '藥丸按鈕 v2' }
  ],
  Timer: [
    { id: 'Style01', name: 'GridExpand', description: '可展開網格計時器' },
    { id: 'Style02', name: 'GridSimple', description: '簡易網格計時器' },
    { id: 'Style03', name: 'Pills', description: '藥丸按鈕計時器' },
    { id: 'Style04', name: 'Dropdown', description: '下拉選單' }
  ],
  CustomToggles: [
    { id: 'Style01', name: 'Switches', description: '開關組' },
    { id: 'Style02', name: 'HorizontalSwitch', description: '水平開關' },
    { id: 'Style03', name: 'IconPills', description: '圖標藥丸' }
  ],
  HumiditySetting: [
    { id: 'Style01', name: 'Slider', description: '滑桿' },
    { id: 'Style02', name: 'Stepper', description: '步進器' },
    { id: 'Style03', name: 'SliderMarks', description: '帶刻度滑桿' },
    { id: 'Style04', name: 'Stepper', description: '步進器 v2' }
  ],
  TemperatureSetting: [
    { id: 'Style01', name: 'Stepper', description: '溫度步進器' }
  ],
  ThermostatMode: [
    { id: 'Style01', name: 'IconButtons', description: '圖標按鈕' }
  ],
  OpenClose: [
    { id: 'Style01', name: 'Slider', description: '開合滑桿' },
    { id: 'Style02', name: 'Buttons', description: '開合按鈕' }
  ]
};

// ============================================
// Attribute Style Registry
// ============================================

/**
 * Registry of available styles per attribute
 */
export const ATTRIBUTE_STYLE_REGISTRY: Record<AttributeName, StyleInfo[]> = {
  SensorDisplay: [
    { id: 'Style01', name: 'Card', description: '卡片顯示' },
    { id: 'Style02', name: 'Inline', description: '行內顯示' },
    { id: 'Style03', name: 'Gauge', description: '儀表顯示' },
    { id: 'Style04', name: 'Circle', description: '圓形顯示' },
    { id: 'Style05', name: 'Rectangle', description: '矩形顯示' },
    { id: 'Style06', name: 'CardAlt', description: '卡片顯示（替代）' }
  ],
  SensorChart: [
    { id: 'Style01', name: 'Line', description: '折線圖' },
    { id: 'Style02', name: 'Area', description: '面積圖' },
    { id: 'Style03', name: 'DualAxis', description: '雙軸圖' }
  ],
  StatusIndicator: [
    { id: 'Style01', name: 'Badge', description: '徽章指示器（有背景色）' },
    { id: 'Style02', name: 'Dot', description: '圓點指示器' },
    { id: 'Style03', name: 'Outline', description: '邊框指示器（無背景色）' }
  ],
  OccupancyTimeline: [
    { id: 'Style01', name: 'Timeline', description: '時間軸' }
  ],
  AQIDisplay: [
    { id: 'Style01', name: 'Card', description: 'AQI 卡片式顯示（PM2.5、CO2、TVOC）' },
    { id: 'Style02', name: 'Gauge', description: 'AQI 儀表板（圓形顯示）' }
  ]
};

// ============================================
// Device Type Display Names
// ============================================

export const DEVICE_TYPE_DISPLAY_NAMES: Record<string, { zh: string; en: string }> = {
  light: { zh: '燈光', en: 'Light' },
  hvac: { zh: '冷氣', en: 'HVAC' },
  th_sensor: { zh: '溫濕度感應器', en: 'TH Sensor' },
  presence_sensor: { zh: '人體感應器', en: 'Presence Sensor' },
  lux_sensor: { zh: '光照感應器', en: 'Lux Sensor' },
  multi_sensor: { zh: '多功能感應器', en: 'Multi Sensor' },
  dehumidifier: { zh: '除濕機', en: 'Dehumidifier' },
  switch: { zh: '開關', en: 'Switch' },
  outlet: { zh: '插座', en: 'Outlet' },
  fan: { zh: '風扇', en: 'Fan' },
  curtain: { zh: '窗簾', en: 'Curtain' },
  blind: { zh: '百葉窗', en: 'Blind' },
  power_meter: { zh: '電力錶', en: 'Power Meter' },
  power_meter_3phase: { zh: '三相電力錶', en: '3-Phase Power Meter' },
  door_sensor: { zh: '門磁', en: 'Door Sensor' },
  button: { zh: '按鈕', en: 'Button' },
  erv: { zh: '全熱交換機', en: 'ERV' },
  generic: { zh: '通用', en: 'Generic' }
};

// ============================================
// Traits used by each device type
// ============================================

export const DEVICE_TYPE_TRAITS: Record<string, TraitName[]> = {
  light: ['OnOff', 'Brightness', 'ColorSetting'],
  hvac: ['OnOff', 'TemperatureSetting', 'ThermostatMode', 'FanSpeed', 'Mode', 'CustomToggles'],
  th_sensor: [],
  presence_sensor: [],
  lux_sensor: [],
  multi_sensor: [],
  dehumidifier: ['OnOff', 'HumiditySetting', 'FanSpeed', 'Mode', 'Timer', 'CustomToggles'],
  switch: ['OnOff'],
  outlet: ['OnOff'],
  fan: ['OnOff', 'FanSpeed', 'Mode', 'Timer'],
  curtain: ['OpenClose'],
  blind: ['OpenClose'],
  power_meter: [],
  power_meter_3phase: [],
  door_sensor: [],
  button: [],
  erv: ['OnOff', 'FanSpeed', 'ThermostatMode', 'Mode', 'Timer'],
  generic: ['OnOff']
};

// ============================================
// Attributes used by each device type
// ============================================

export const DEVICE_TYPE_ATTRIBUTES: Record<string, AttributeName[]> = {
  light: ['StatusIndicator'],
  hvac: ['SensorDisplay', 'StatusIndicator'],
  th_sensor: ['SensorDisplay', 'SensorChart', 'StatusIndicator'],
  presence_sensor: ['SensorDisplay', 'OccupancyTimeline', 'StatusIndicator'],
  lux_sensor: ['SensorDisplay', 'SensorChart', 'StatusIndicator'],
  multi_sensor: ['SensorDisplay', 'SensorChart', 'OccupancyTimeline', 'StatusIndicator'],
  dehumidifier: ['SensorDisplay', 'StatusIndicator'],
  switch: ['SensorDisplay', 'SensorChart', 'StatusIndicator'],
  outlet: ['SensorDisplay', 'SensorChart', 'StatusIndicator'],
  fan: ['StatusIndicator'],
  curtain: ['StatusIndicator'],
  blind: ['StatusIndicator'],
  power_meter: ['SensorDisplay', 'SensorChart', 'StatusIndicator'],
  power_meter_3phase: ['SensorDisplay', 'SensorChart', 'StatusIndicator'],
  door_sensor: ['OccupancyTimeline', 'StatusIndicator'],
  button: ['SensorChart', 'StatusIndicator'],
  erv: ['SensorDisplay', 'AQIDisplay', 'SensorChart', 'StatusIndicator'],
  generic: ['SensorDisplay', 'SensorChart', 'StatusIndicator']
};

// ============================================
// Default Configurations
// ============================================

export const DEFAULT_TRAIT_STYLES: TraitStyleConfig = {
  OnOff: 'Style02',
  Brightness: 'Style01',
  HumiditySetting: 'Style02',
  FanSpeed: 'Style01',
  Mode: 'Style01',
  Timer: 'Style01',
  CustomToggles: 'Style01'
};

export const DEFAULT_ATTRIBUTE_STYLES: AttributeStyleConfig = {
  SensorDisplay: 'Style01',
  SensorChart: 'Style02',
  StatusIndicator: 'Style02'
};

// ============================================
// Icon Style Registry
// ============================================

export const ICON_STYLE_REGISTRY: Record<string, StyleInfo[]> = {
  light: [
    { id: 'Style01', name: 'Lightbulb', description: 'Classic lightbulb icon' },
    { id: 'Style02', name: 'LightMode', description: 'Sun/light mode icon' },
  ],
  switch: [
    { id: 'Style01', name: 'Switch', description: 'Toggle switch icon' },
    { id: 'Style02', name: 'ToggleOn', description: 'Toggle on icon' },
  ],
  outlet: [
    { id: 'Style01', name: 'SmartOutlet', description: 'Smart outlet icon' },
    { id: 'Style02', name: 'Power', description: 'Power plug icon' },
  ],
  hvac: [
    { id: 'Style01', name: 'AcUnit', description: 'Air conditioner unit' },
    { id: 'Style02', name: 'AirConditioning', description: 'AC wave icon' },
  ],
  fan: [
    { id: 'Style01', name: 'Fan', description: 'Fan icon' },
    { id: 'Style02', name: 'Air', description: 'Wind/air flow icon' },
    { id: 'Style03', name: 'Windmill', description: 'Windmill/pinwheel icon' },
    { id: 'Style04', name: 'Hvac', description: 'HVAC/ventilation icon' },
  ],
  th_sensor: [
    { id: 'Style01', name: 'Thermometer', description: 'Temperature icon' },
    { id: 'Style02', name: 'WaterDrop', description: 'Humidity icon' },
  ],
  presence_sensor: [
    { id: 'Style01', name: 'SensorOccupied', description: 'Occupied sensor' },
    { id: 'Style02', name: 'Radio', description: 'Radio waves icon' },
  ],
  door_sensor: [
    { id: 'Style01', name: 'Door', description: 'Door icon' },
    { id: 'Style02', name: 'SensorDoor', description: 'Sensor door icon' },
  ],
  curtain: [
    { id: 'Style01', name: 'RollerShades', description: 'Roller shades icon' },
    { id: 'Style02', name: 'Curtains', description: 'Curtains open icon' },
    { id: 'Style03', name: 'CurtainsClosed', description: 'Curtains closed icon' },
    { id: 'Style04', name: 'VerticalShades', description: 'Vertical shades icon' },
    { id: 'Style05', name: 'Window', description: 'Window icon' },
  ],
  blind: [
    { id: 'Style01', name: 'Blinds', description: 'Horizontal blinds' },
    { id: 'Style02', name: 'VerticalShades', description: 'Vertical shades' },
  ],
  power_meter: [
    { id: 'Style01', name: 'NetworkCheck', description: 'Network meter icon' },
    { id: 'Style02', name: 'ElectricMeter', description: 'Electric meter icon' },
  ],
  power_meter_3phase: [
    { id: 'Style01', name: 'ThreePhase', description: '3-phase power icon' },
    { id: 'Style02', name: 'Bolt', description: 'Lightning bolt icon' },
    { id: 'Style03', name: 'Power', description: 'Power symbol icon' },
    { id: 'Style04', name: 'NetworkCheck', description: 'Network meter icon' },
  ],
  dehumidifier: [
    { id: 'Style01', name: 'WaterDrop', description: 'Water drop icon' },
    { id: 'Style02', name: 'Humidity', description: 'Humidity percentage icon' },
    { id: 'Style03', name: 'Dew', description: 'Dew point icon' },
    { id: 'Style04', name: 'Waves', description: 'Waves icon' },
  ],
  button: [
    { id: 'Style01', name: 'TouchApp', description: 'Touch button icon' },
  ],
  multi_sensor: [
    { id: 'Style01', name: 'Sensors', description: 'Multi sensor icon' },
  ],
  lux_sensor: [
    { id: 'Style01', name: 'LightMode', description: 'Light sensor icon' },
    { id: 'Style02', name: 'Sunny', description: 'Sunny icon' },
    { id: 'Style03', name: 'Brightness', description: 'Brightness icon' },
    { id: 'Style04', name: 'WbSunny', description: 'WB Sunny icon' },
  ],
  erv: [
    { id: 'Style01', name: 'Air', description: '氣流波紋' },
    { id: 'Style02', name: 'HeatExchange', description: '熱交換雙向箭頭' },
    { id: 'Style03', name: 'AirPurifier', description: '濾網設備' },
    { id: 'Style04', name: 'Fan', description: '旋轉扇葉' },
  ],
  generic: [
    { id: 'Style01', name: 'Device', description: 'Generic device icon' },
  ],
};

/**
 * Get available icon styles for a device type
 */
export function getAvailableIconStyles(deviceType: string): StyleInfo[] {
  return ICON_STYLE_REGISTRY[deviceType] || ICON_STYLE_REGISTRY.generic || [];
}

/**
 * Get default config for a device type
 * @param deviceType The device type
 * @param templateId Optional template ID to get template-specific defaults
 */
export function getDefaultConfig(deviceType: string, templateId?: string): DeviceModalConfig {
  const templates = TEMPLATE_REGISTRY[deviceType] || TEMPLATE_REGISTRY.generic;
  const targetTemplateId = templateId || templates[0]?.id || 'Template01';
  
  // Find the template to get its default config
  const template = templates.find(t => t.id === targetTemplateId);
  const templateDefaultConfig = template?.defaultConfig;
  
  return {
    templateId: targetTemplateId as TemplateStyleId,
    traitStyles: { 
      ...DEFAULT_TRAIT_STYLES, 
      ...(templateDefaultConfig?.traitStyles || {})
    },
    attributeStyles: { 
      ...DEFAULT_ATTRIBUTE_STYLES, 
      ...(templateDefaultConfig?.attributeStyles || {})
    },
    iconStyle: (templateDefaultConfig?.iconStyle || 'Style01') as TraitStyleId,
    smallIconStyle: (templateDefaultConfig?.smallIconStyle || 'Style01') as TraitStyleId,
    bigIconStyle: (templateDefaultConfig?.bigIconStyle || 'Style01') as TraitStyleId,
    traitOrder: templateDefaultConfig?.traitOrder,
    hiddenComponents: templateDefaultConfig?.hiddenComponents
  };
}

// ============================================
// API Functions
// ============================================

/**
 * Get available templates for a device type
 */
export function getAvailableTemplates(deviceType: string): TemplateInfo[] {
  return TEMPLATE_REGISTRY[deviceType] || TEMPLATE_REGISTRY.generic || [];
}

/**
 * Get available styles for a trait
 */
export function getAvailableTraitStyles(traitName: TraitName): StyleInfo[] {
  return TRAIT_STYLE_REGISTRY[traitName] || [];
}

/**
 * Get available styles for an attribute
 */
export function getAvailableAttributeStyles(attributeName: AttributeName): StyleInfo[] {
  return ATTRIBUTE_STYLE_REGISTRY[attributeName] || [];
}

/**
 * Get traits used by a device type
 */
export function getDeviceTypeTraits(deviceType: string): TraitName[] {
  return DEVICE_TYPE_TRAITS[deviceType] || DEVICE_TYPE_TRAITS.generic || [];
}

/**
 * Get attributes used by a device type
 */
export function getDeviceTypeAttributes(deviceType: string): AttributeName[] {
  return DEVICE_TYPE_ATTRIBUTES[deviceType] || DEVICE_TYPE_ATTRIBUTES.generic || [];
}

/**
 * Get all supported device types
 */
export function getAllDeviceTypes(): string[] {
  return Object.keys(TEMPLATE_REGISTRY);
}

/**
 * Delete a template from the registry
 * Returns true if deleted successfully, false if cannot delete (only one template left)
 */
export function deleteTemplate(deviceType: string, templateId: string): boolean {
  const templates = TEMPLATE_REGISTRY[deviceType];
  if (!templates || templates.length <= 1) {
    // Cannot delete if only one template or device type not found
    return false;
  }
  
  const index = templates.findIndex(t => t.id === templateId);
  if (index === -1) {
    // Template not found
    return false;
  }
  
  // Remove the template from the registry
  TEMPLATE_REGISTRY[deviceType] = templates.filter(t => t.id !== templateId);
  
  return true;
}

/**
 * Add a new template to the registry
 */
export function addTemplate(deviceType: string, template: TemplateInfo): boolean {
  if (!TEMPLATE_REGISTRY[deviceType]) {
    TEMPLATE_REGISTRY[deviceType] = [];
  }
  
  // Check if template already exists
  const exists = TEMPLATE_REGISTRY[deviceType].some(t => t.id === template.id);
  if (exists) {
    return false;
  }
  
  TEMPLATE_REGISTRY[deviceType].push(template);
  return true;
}

// ============================================
// Firestore Integration
// ============================================

// Cache for theme configs (uses DeviceModalConfig after conversion)
let themeConfigCache: Record<string, DeviceModalConfig> | null = null;
let themeCacheTimestamp = 0;
const THEME_CACHE_TTL = 60000; // 1 minute

/**
 * Convert Firestore data to DeviceModalConfig with proper types
 */
function convertFirestoreToConfig(data: FirestoreDeviceModalConfig): DeviceModalConfig {
  return {
    templateId: (data.templateId || 'Template01') as TemplateStyleId,
    templateName: data.templateName, // User-customized display name (optional)
    traitStyles: data.traitStyles ? Object.fromEntries(
      Object.entries(data.traitStyles).map(([k, v]) => [k, v as TraitStyleId])
    ) as TraitStyleConfig : undefined,
    attributeStyles: data.attributeStyles ? Object.fromEntries(
      Object.entries(data.attributeStyles).map(([k, v]) => [k, v as TraitStyleId])
    ) as AttributeStyleConfig : undefined,
    iconStyle: data.iconStyle as TraitStyleId | undefined, // Legacy: Device icon style
    smallIconStyle: data.smallIconStyle as TraitStyleId | undefined, // Small icon (title bar)
    bigIconStyle: data.bigIconStyle as TraitStyleId | undefined, // Big icon (main display)
    traitOrder: data.traitOrder, // Custom order of trait/attribute components
    hiddenComponents: data.hiddenComponents // Hidden components list
  };
}

/**
 * Get the currently APPLIED device modal theme config from Firestore
 */
export async function getDeviceModalTheme(deviceType: string): Promise<DeviceModalConfig> {
  try {
    // Check cache first
    const now = Date.now();
    if (themeConfigCache && (now - themeCacheTimestamp) < THEME_CACHE_TTL) {
      if (themeConfigCache[deviceType]) {
        return themeConfigCache[deviceType];
      }
    }

    // Fetch from Firestore
    const preferences = await getUserPreferences();
    const rawThemes = preferences.deviceModalThemes || {};
    const rawData = rawThemes[deviceType];
    
    let appliedConfig: DeviceModalConfig;
    
    if (isNewStorageFormat(rawData)) {
      // New format: get the applied template's config
      const appliedTemplateId = rawData.appliedTemplateId;
      const templateConfig = rawData.templateConfigs[appliedTemplateId];
      if (templateConfig) {
        appliedConfig = convertFirestoreToConfig(templateConfig);
      } else {
        appliedConfig = getDefaultConfig(deviceType, appliedTemplateId);
      }
    } else if (rawData) {
      // Old format: single config (migrate on next save)
      appliedConfig = convertFirestoreToConfig(rawData as FirestoreDeviceModalConfig);
    } else {
      appliedConfig = getDefaultConfig(deviceType);
    }
    
    // Merge with defaults
    const defaultConfig = getDefaultConfig(deviceType, appliedConfig.templateId);
    const mergedConfig = {
      ...defaultConfig,
      ...appliedConfig,
      traitStyles: { ...defaultConfig.traitStyles, ...appliedConfig?.traitStyles },
      attributeStyles: { ...defaultConfig.attributeStyles, ...appliedConfig?.attributeStyles }
    };
    
    // Update cache
    if (!themeConfigCache) themeConfigCache = {};
    themeConfigCache[deviceType] = mergedConfig;
    themeCacheTimestamp = now;

    return mergedConfig;
  } catch (error) {
    logger.warn('Failed to get device modal theme, using default:', error);
    return getDefaultConfig(deviceType);
  }
}

/**
 * Get a specific template's config (not necessarily the applied one)
 */
export async function getTemplateConfig(deviceType: string, templateId: string): Promise<DeviceModalConfig> {
  try {
    const preferences = await getUserPreferences();
    const rawThemes = preferences.deviceModalThemes || {};
    const rawData = rawThemes[deviceType];
    
    if (isNewStorageFormat(rawData)) {
      // New format: get specific template's config
      const templateConfig = rawData.templateConfigs[templateId];
      if (templateConfig) {
        const savedConfig = convertFirestoreToConfig(templateConfig);
        const defaultConfig = getDefaultConfig(deviceType, templateId);
        return {
          ...defaultConfig,
          ...savedConfig,
          traitStyles: { ...defaultConfig.traitStyles, ...savedConfig?.traitStyles },
          attributeStyles: { ...defaultConfig.attributeStyles, ...savedConfig?.attributeStyles }
        };
      }
    } else if (rawData && (rawData as FirestoreDeviceModalConfig).templateId === templateId) {
      // Old format: only if this is the same template
      const savedConfig = convertFirestoreToConfig(rawData as FirestoreDeviceModalConfig);
      const defaultConfig = getDefaultConfig(deviceType, templateId);
      return {
        ...defaultConfig,
        ...savedConfig,
        traitStyles: { ...defaultConfig.traitStyles, ...savedConfig?.traitStyles },
        attributeStyles: { ...defaultConfig.attributeStyles, ...savedConfig?.attributeStyles }
      };
    }
    
    // No saved config for this template, return default
    return getDefaultConfig(deviceType, templateId);
  } catch (error) {
    logger.warn('Failed to get template config, using default:', error);
    return getDefaultConfig(deviceType, templateId);
  }
}

/**
 * Get the currently applied template ID for a device type
 */
export async function getAppliedTemplateId(deviceType: string): Promise<string> {
  try {
    const preferences = await getUserPreferences();
    const rawThemes = preferences.deviceModalThemes || {};
    const rawData = rawThemes[deviceType];
    
    if (isNewStorageFormat(rawData)) {
      return rawData.appliedTemplateId;
    } else if (rawData) {
      return (rawData as FirestoreDeviceModalConfig).templateId || 'Template01';
    }
    return 'Template01';
  } catch (error) {
    return 'Template01';
  }
}

/**
 * Get all applied device modal themes from Firestore
 * Returns the currently APPLIED config for each device type
 */
export async function getAllDeviceModalThemes(): Promise<Record<string, DeviceModalConfig>> {
  try {
    const preferences = await getUserPreferences();
    const rawThemes = preferences.deviceModalThemes || {};
    
    // Convert themes
    const themes: Record<string, DeviceModalConfig> = {};
    for (const [type, rawData] of Object.entries(rawThemes)) {
      if (isNewStorageFormat(rawData)) {
        // New format: get applied template's config
        const appliedId = rawData.appliedTemplateId;
        const templateConfig = rawData.templateConfigs[appliedId];
        if (templateConfig) {
          themes[type] = convertFirestoreToConfig(templateConfig);
        } else {
          themes[type] = getDefaultConfig(type, appliedId);
        }
      } else {
        // Old format
        themes[type] = convertFirestoreToConfig(rawData as FirestoreDeviceModalConfig);
      }
    }
    
    return themes;
  } catch (error) {
    logger.warn('Failed to get all device modal themes:', error);
    return {};
  }
}

/**
 * Get all template configs for a device type (including non-applied)
 */
export async function getAllTemplateConfigs(deviceType: string): Promise<Record<string, DeviceModalConfig>> {
  try {
    const preferences = await getUserPreferences();
    const rawThemes = preferences.deviceModalThemes || {};
    const rawData = rawThemes[deviceType];
    
    const result: Record<string, DeviceModalConfig> = {};
    
    if (isNewStorageFormat(rawData)) {
      // New format: return all template configs
      for (const [templateId, templateConfig] of Object.entries(rawData.templateConfigs)) {
        const defaultConfig = getDefaultConfig(deviceType, templateId);
        const savedConfig = convertFirestoreToConfig(templateConfig);
        result[templateId] = {
          ...defaultConfig,
          ...savedConfig,
          traitStyles: { ...defaultConfig.traitStyles, ...savedConfig?.traitStyles },
          attributeStyles: { ...defaultConfig.attributeStyles, ...savedConfig?.attributeStyles }
        };
      }
    } else if (rawData) {
      // Old format: only one config
      const config = convertFirestoreToConfig(rawData as FirestoreDeviceModalConfig);
      result[config.templateId] = config;
    }
    
    return result;
  } catch (error) {
    logger.warn('Failed to get all template configs:', error);
    return {};
  }
}

/**
 * Save a template's config WITHOUT changing which template is applied
 * Use this for "儲存" (Save) button
 */
export async function saveTemplateConfig(
  deviceType: string,
  templateId: string,
  config: DeviceModalConfig
): Promise<void> {
  try {
    const preferences = await getUserPreferences();
    const themes = preferences.deviceModalThemes || {};
    const existingData = themes[deviceType];
    
    // Prepare the template config for storage
    const templateConfig: FirestoreDeviceModalConfig = {
      templateId: templateId,
      templateName: config.templateName,
      traitStyles: config.traitStyles as Record<string, string> | undefined,
      attributeStyles: config.attributeStyles as Record<string, string> | undefined,
      iconStyle: config.iconStyle,
      smallIconStyle: config.smallIconStyle,
      bigIconStyle: config.bigIconStyle,
      traitOrder: config.traitOrder,
      hiddenComponents: config.hiddenComponents
    };
    
    let newStorage: FirestoreDeviceThemeStorage;
    
    if (isNewStorageFormat(existingData)) {
      // Already new format: update the specific template's config
      newStorage = {
        appliedTemplateId: existingData.appliedTemplateId,
        templateConfigs: {
          ...existingData.templateConfigs,
          [templateId]: templateConfig
        }
      };
    } else if (existingData) {
      // Migrate from old format
      const oldConfig = existingData as FirestoreDeviceModalConfig;
      newStorage = {
        appliedTemplateId: oldConfig.templateId, // Keep old applied template
        templateConfigs: {
          [oldConfig.templateId]: oldConfig, // Preserve old config
          [templateId]: templateConfig // Add new config
        }
      };
    } else {
      // No existing data
      newStorage = {
        appliedTemplateId: templateId, // Default to this template
        templateConfigs: {
          [templateId]: templateConfig
        }
      };
    }
    
    themes[deviceType] = newStorage as any;
    
    await saveUserPreferences({
      ...preferences,
      deviceModalThemes: themes
    });

    // Invalidate cache
    invalidateThemeCache();

    logger.log(`✅ Template config saved for ${deviceType}/${templateId}`);
  } catch (error) {
    logger.error(`Failed to save template config for ${deviceType}/${templateId}:`, error);
    throw error;
  }
}

/**
 * Apply a template (change which template is active) AND save its config
 * Use this for "儲存並套用" (Save & Apply) button
 */
export async function saveDeviceModalTheme(
  deviceType: string, 
  config: DeviceModalConfig
): Promise<void> {
  try {
    const preferences = await getUserPreferences();
    const themes = preferences.deviceModalThemes || {};
    const existingData = themes[deviceType];
    const templateId = config.templateId;
    
    // Prepare the template config for storage
    const templateConfig: FirestoreDeviceModalConfig = {
      templateId: templateId,
      templateName: config.templateName,
      traitStyles: config.traitStyles as Record<string, string> | undefined,
      attributeStyles: config.attributeStyles as Record<string, string> | undefined,
      iconStyle: config.iconStyle,
      smallIconStyle: config.smallIconStyle,
      bigIconStyle: config.bigIconStyle,
      traitOrder: config.traitOrder,
      hiddenComponents: config.hiddenComponents
    };
    
    let newStorage: FirestoreDeviceThemeStorage;
    
    if (isNewStorageFormat(existingData)) {
      // Already new format: update config and change applied template
      newStorage = {
        appliedTemplateId: templateId, // Change applied template
        templateConfigs: {
          ...existingData.templateConfigs,
          [templateId]: templateConfig
        }
      };
    } else if (existingData) {
      // Migrate from old format
      const oldConfig = existingData as FirestoreDeviceModalConfig;
      newStorage = {
        appliedTemplateId: templateId, // Apply new template
        templateConfigs: {
          [oldConfig.templateId]: oldConfig, // Preserve old config
          [templateId]: templateConfig
        }
      };
    } else {
      // No existing data
      newStorage = {
        appliedTemplateId: templateId,
        templateConfigs: {
          [templateId]: templateConfig
        }
      };
    }
    
    themes[deviceType] = newStorage as any;
    
    await saveUserPreferences({
      ...preferences,
      deviceModalThemes: themes
    });

    // Update cache with converted config
    if (!themeConfigCache) themeConfigCache = {};
    themeConfigCache[deviceType] = config;
    themeCacheTimestamp = Date.now();

    logger.log(`✅ Device modal theme saved and applied for ${deviceType}:`, config);
  } catch (error) {
    logger.error(`Failed to save device modal theme for ${deviceType}:`, error);
    throw error;
  }
}

/**
 * Apply a template without modifying its config
 * Just changes which template is currently active
 */
export async function applyTemplate(deviceType: string, templateId: string): Promise<void> {
  try {
    const preferences = await getUserPreferences();
    const themes = preferences.deviceModalThemes || {};
    const existingData = themes[deviceType];
    
    let newStorage: FirestoreDeviceThemeStorage;
    
    if (isNewStorageFormat(existingData)) {
      // New format: just change applied template
      newStorage = {
        ...existingData,
        appliedTemplateId: templateId
      };
    } else if (existingData) {
      // Migrate from old format
      const oldConfig = existingData as FirestoreDeviceModalConfig;
      newStorage = {
        appliedTemplateId: templateId,
        templateConfigs: {
          [oldConfig.templateId]: oldConfig
        }
      };
    } else {
      // No existing data
      newStorage = {
        appliedTemplateId: templateId,
        templateConfigs: {}
      };
    }
    
    themes[deviceType] = newStorage as any;
    
    await saveUserPreferences({
      ...preferences,
      deviceModalThemes: themes
    });

    // Invalidate cache
    invalidateThemeCache();

    logger.log(`✅ Template ${templateId} applied for ${deviceType}`);
  } catch (error) {
    logger.error(`Failed to apply template for ${deviceType}:`, error);
    throw error;
  }
}

/**
 * Reset device modal theme to default
 */
export async function resetDeviceModalTheme(deviceType: string): Promise<void> {
  try {
    const preferences = await getUserPreferences();
    const themes = preferences.deviceModalThemes || {};
    
    delete themes[deviceType];
    
    await saveUserPreferences({
      ...preferences,
      deviceModalThemes: themes
    });

    // Invalidate cache for this device type
    if (themeConfigCache) {
      delete themeConfigCache[deviceType];
    }
    themeCacheTimestamp = Date.now();

    logger.log(`✅ Device modal theme reset to default for ${deviceType}`);
  } catch (error) {
    logger.error(`Failed to reset device modal theme for ${deviceType}:`, error);
    throw error;
  }
}

/**
 * Invalidate theme cache (call after external updates)
 */
export function invalidateThemeCache(): void {
  themeConfigCache = null;
  themeCacheTimestamp = 0;
}
